import { useProductColorsSelloutSummary } from '../../../../../hooks/useProductColorsSelloutSummary';
import { ProductColorsSelloutSummary } from '../../../../../hooks/useProductColorsSelloutSummary.types';
import { saleItemIsProductColor } from '../../../utils/saleItemType.utils';
import {
  CatalogSelloutMap,
  PagesById,
  UseCatalogSelloutParams,
} from '../catalogProductsListV2.types';

const getPageProductColorIds = (pagesById: PagesById, page: number): string[] =>
  (pagesById.get(page)?.data ?? []).reduce<string[]>((productColorIds, esProduct) => {
    if (saleItemIsProductColor(esProduct.item)) {
      productColorIds.push(esProduct.item.productColorId);
    }

    return productColorIds;
  }, []);

const mergeToMap = (
  pages: { productColorIds: string[]; sellout: ProductColorsSelloutSummary }[],
): CatalogSelloutMap => {
  const selloutMap: CatalogSelloutMap = new Map();

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const { productColorIds, sellout } = pages[pageIndex];

    for (let idIndex = 0; idIndex < productColorIds.length; idIndex++) {
      const productColorId = productColorIds[idIndex];
      const summary = sellout.getSummary(productColorId);

      if (summary.hasSelloutValues) {
        selloutMap.set(productColorId, {
          productColorId,
          totalQuantitySold: summary.totalQuantitySold,
        });
      }
    }
  }

  return selloutMap;
};

export const useCatalogSellout = (params: UseCatalogSelloutParams): CatalogSelloutMap => {
  const { pagesById, anchorPage, orderId } = params;

  const behindIds = getPageProductColorIds(pagesById, anchorPage - 1);
  const currentIds = getPageProductColorIds(pagesById, anchorPage);
  const aheadIds = getPageProductColorIds(pagesById, anchorPage + 1);
  const aheadNextIds = getPageProductColorIds(pagesById, anchorPage + 2);

  const behindSellout = useProductColorsSelloutSummary({
    productColorIds: behindIds,
    orderId,
    isSummaryPreservedWhileFetching: true,
  });
  const currentSellout = useProductColorsSelloutSummary({
    productColorIds: currentIds,
    orderId,
    isSummaryPreservedWhileFetching: true,
  });
  const aheadSellout = useProductColorsSelloutSummary({
    productColorIds: aheadIds,
    orderId,
    isSummaryPreservedWhileFetching: true,
  });
  const aheadNextSellout = useProductColorsSelloutSummary({
    productColorIds: aheadNextIds,
    orderId,
    isSummaryPreservedWhileFetching: true,
  });

  return mergeToMap([
    { productColorIds: behindIds, sellout: behindSellout },
    { productColorIds: currentIds, sellout: currentSellout },
    { productColorIds: aheadIds, sellout: aheadSellout },
    { productColorIds: aheadNextIds, sellout: aheadNextSellout },
  ]);
};
